import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Search,
  PencilRuler,
  LineChart,
  HardHat,
  PackageCheck,
  Home,
  Store,
  Layers,
  Recycle,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { useDocumentSEO } from "@/hooks/useDocumentSEO";

const INTRO_IMAGE =
  "https://images.unsplash.com/photo-1721244654394-36a7bc2da288?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxlbmdpbmVlcmluZyUyMGJsdWVwcmludCUyMGFyY2hpdGVjdHVyYWwlMjBwbGFufGVufDB8fHx8MTc4MzIyOTk0Mnww&ixlib=rb-4.1.0&q=85&w=1400";

const STEPS = [
  {
    n: "01",
    icon: Search,
    title: "İhtiyaç Analizi",
    lead:
      "Projenin amacı, ölçeği, bütçesi ve hedef kullanım senaryosunun detaylı analiziyle başlıyoruz.",
    body: "Bu aşama, ilerleyen tüm kararların üzerine oturacağı zemini kurar; hedef, kısıt ve öncelikleri baştan netleştiririz. Beklenti ile mümkün olan arasındaki mesafeyi objektif biçimde tanımlayarak, süreci sağlıklı bir zeminde başlatırız.",
    chips: ["Hedef & Kısıt", "Bütçe Çerçevesi", "Kullanım Senaryosu"],
  },
  {
    n: "02",
    icon: PencilRuler,
    title: "Mimari Planlama",
    lead:
      "İhtiyaç analizine dayanarak mimari konsept ve uygulama projelerini geliştiriyoruz.",
    body: "Kararları; işlev, malzeme ve maliyeti aynı masada değerlendirerek şekillendirir; tasarım aşamasında uygulama gerçekliğini de göz önünde bulundururuz. Amaç, sahaya sorunsuz aktarılabilen ve mimari değeri koruyan bir proje ortaya koymaktır.",
    chips: ["Konsept", "Uygulama Projesi", "Malzeme Kararları"],
  },
  {
    n: "03",
    icon: LineChart,
    title: "Bütçe ve Süreç Yönetimi",
    lead:
      "Maliyet planlaması ve zaman çizelgesini netleştirerek süreç kontrolünü baştan kuruyoruz.",
    body: "Kritik yol, tedarik programı ve karar noktaları izlenebilir bir çerçeveye oturur; sürpriz maliyet ve gecikmelerin kaynağı erken aşamada tanımlanır. Süreç boyunca düzenli raporlamayla bütçe ve program disiplinini korumaya öncelik veririz.",
    chips: ["Maliyet Planı", "Kritik Yol", "Karar Noktaları"],
  },
  {
    n: "04",
    icon: HardHat,
    title: "Uygulama ve Koordinasyon",
    lead:
      "İnşaat ve uygulama sürecini saha ekipleri, tedarikçiler ve paydaşlarla koordineli biçimde yürütüyoruz.",
    body: "Saha kalite kontrolü ve düzenli koordinasyon toplantıları süreç boyunca devam eder; teknik değişiklik ihtiyaçları planlı bir prosedürle yönetilir. Amacımız, tasarımda alınan kararların sahada aynı disiplinle karşılık bulmasıdır.",
    chips: ["Saha Koordinasyonu", "Kalite Kontrol", "Paydaş Yönetimi"],
  },
  {
    n: "05",
    icon: PackageCheck,
    title: "Teslim ve Değer Yönetimi",
    lead:
      "Projeyi zamanında teslim ediyor, uzun vadeli değerini koruyacak şekilde süreci tamamlıyoruz.",
    body: "Kabul süreci; teknik dokümantasyon, kullanım kılavuzları ve garanti kapsamının netleştirilmesiyle desteklenir. Teslim, işin bitişi değil; projenin uzun ömürlü değeriyle yaşamaya başladığı andır.",
    chips: ["Kabul Süreci", "Dokümantasyon", "Değer Sürekliliği"],
  },
];

const COZUMLER = [
  {
    icon: Home,
    title: "Konut Projeleri Yaklaşımı",
    body:
      "Konut projelerinde yaşam kalitesi, planlama disiplini ve maliyet dengesini birlikte ele alırız. Mekân kurgusunda gündelik kullanım senaryolarını ve uzun soluklu değer öğelerini önceliklendirir; yatırım–kullanım dengesini gözeten kararlar üretiriz.",
  },
  {
    icon: Store,
    title: "Ticari Yapı Çözümleri",
    body:
      "Ticari yapılarda işlev, verim ve marka temsiliyetinin ölçüsünü hassasiyetle kurarız. Kullanıcı akışı, teknik altyapı ve enerji verimliliği bir arada değerlendirilir; işletme sürekliliğine hizmet eden tasarım ve uygulama kararları alınır.",
  },
  {
    icon: Layers,
    title: "Karma Kullanım Projeleri",
    body:
      "Karma kullanımlı yapılarda farklı işlevlerin bir arada uyumlu bir bütün oluşturmasını amaçlarız. Konut, ticari ve sosyal alanların erişim, mahremiyet ve program dengesi baştan kurgulanır; her bileşenin kendi standardını korumasını sağlarız.",
  },
  {
    icon: Recycle,
    title: "Dönüşüm Projeleri Yaklaşımı",
    body:
      "Dönüşüm projelerinde mevcut yapının ve çevrenin belleğini gözeterek çalışırız. Hukuki ve teknik zeminin başta netleştirildiği bir çerçevede; güçlendirme, yenileme ve yeniden kullanım kararlarını birlikte değerlendiririz.",
  },
];

/**
 * A single vertical-timeline step. Uses a left-aligned gold rail with a
 * small filled dot on it, and content flowing to the right. No numbers.
 */
function TimelineStep({ step, isLast }) {
  const Icon = step.icon;
  return (
    <li
      data-testid={`surec-step-${step.n}`}
      className="relative pl-12 sm:pl-20 pb-14 sm:pb-20 last:pb-0"
    >
      {/* vertical rail — extends through the item; hidden after last */}
      {!isLast && (
        <span
          aria-hidden
          className="absolute left-[7px] sm:left-[11px] top-2 bottom-0 w-[1px] bg-gradient-to-b from-gold/60 via-gold/40 to-gold/10"
        />
      )}

      {/* Progress marker: small filled dot on the rail */}
      <span
        aria-hidden
        className="absolute left-0 top-1 sm:top-2 flex items-center gap-3 sm:gap-4"
      >
        <span className="w-[15px] h-[15px] sm:w-[23px] sm:h-[23px] rounded-full bg-gold ring-1 ring-gold/30 ring-offset-2 ring-offset-white z-10" />
        <span className="hidden sm:block w-6 h-[1px] bg-gold/60" />
      </span>

      {/* content */}
      <div className="pt-0 sm:pt-1">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-9 h-9 border border-navy/15 flex items-center justify-center text-gold shrink-0">
            <Icon size={16} strokeWidth={1.5} />
          </span>
          <span className="overline text-anthracite/60">Aşama</span>
        </div>

        <h3 className="font-heading text-2xl md:text-3xl lg:text-4xl font-light tracking-tight text-navy leading-tight">
          {step.title}
        </h3>

        <p className="mt-5 text-base md:text-[17px] leading-relaxed text-navy font-light max-w-2xl">
          {step.lead}
        </p>

        <p className="mt-4 text-[15px] leading-relaxed text-anthracite/80 font-light max-w-2xl">
          {step.body}
        </p>

        <div className="mt-6 flex flex-wrap gap-3">
          {step.chips.map((c) => (
            <span
              key={c}
              className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.22em] uppercase font-medium border border-navy/15 text-navy/85"
            >
              <span className="w-1.5 h-1.5 bg-gold" />
              {c}
            </span>
          ))}
        </div>
      </div>
    </li>
  );
}

export default function ProjeYaklasimimizPage() {
  useDocumentSEO({
    title: "Proje Yaklaşımımız",
    description:
      "Projeleri ihtiyaç analizinden teslim ve değer yönetimine kadar 5 aşamalı bir süreçle yönetiyoruz. 1001 Mimarlık İnşaat'ın mimarlık ve inşaat metodolojisi.",
  });
  return (
    <>
      <PageHeader
        breadcrumb="Proje Yaklaşımımız"
        overline="Metodoloji"
        title="Proje Yaklaşımımız"
        subtitle="Projeleri; ihtiyaç analizinden teslim ve değer yönetimine kadar planlı, izlenebilir ve mimari bir bakış açısıyla ele alırız."
        variant="dark"
      />

      {/* 1. GİRİŞ */}
      <section
        data-testid="yaklasim-intro"
        className="bg-white py-24 lg:py-32"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-[1px] bg-gold" />
              <span className="overline text-anthracite/70">Giriş</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-navy leading-tight">
              Mimari bakışı,
              <br />
              <span className="text-gold italic font-extralight">
                ticari disiplinle
              </span>{" "}
              buluşturan bir süreç.
            </h2>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-anthracite/85 leading-relaxed font-light">
              Her projeye, mimari bakış açısını ticari disiplinle birleştiren
              planlı bir süreçle yaklaşıyoruz. Aşağıdaki beş aşama; bir
              projeyi ihtiyaç analizinden teslim ve değer yönetimine kadar
              nasıl yönettiğimizi özetler.
            </p>
            <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-anthracite/70 font-light">
              Süreç, kâğıt üzerinde durmayan bir çerçevedir; her aşama kendi
              çıktıları, karar noktaları ve doğrulama adımlarıyla tanımlıdır.
            </p>
          </div>

          <div className="md:col-span-5">
            <div className="relative overflow-hidden">
              <img
                src={INTRO_IMAGE}
                alt="Mimari plan üzerinde çalışma"
                className="w-full aspect-[4/5] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-navy/25 mix-blend-multiply" />
              <div className="absolute inset-0 border border-gold/40" />
              <div className="absolute top-4 left-4 w-6 h-[1px] bg-gold" />
              <div className="absolute top-4 left-4 w-[1px] h-6 bg-gold" />
              <div className="absolute bottom-4 right-4 w-6 h-[1px] bg-gold" />
              <div className="absolute bottom-4 right-4 w-[1px] h-6 bg-gold" />

              {/* Overlaid corner label */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="overline text-white/70 mb-1">Süreç</div>
                <div className="font-heading text-white text-lg font-light">
                  Beş aşamalı, izlenebilir yaklaşım
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. TİMELİNE */}
      <section
        data-testid="yaklasim-timeline"
        className="bg-white py-20 lg:py-28 relative overflow-hidden"
      >
        <div className="absolute inset-0 blueprint-grid opacity-50 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-[1px] bg-gold" />
                <span className="overline text-anthracite/70">
                  Beş Aşamalı Süreç
                </span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-navy leading-tight">
                Bir projeyi
                <br />
                nasıl yönetiyoruz.
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-base leading-relaxed text-anthracite/80 font-light max-w-lg">
                Aşamaları kâğıda değil, karar noktalarına ve çıktılara bağlı
                bir çerçeveye oturturuz. Her adım kendinden önceki adımın
                çıktısını doğrular, sonraki adımın girdisini hazırlar.
              </p>
            </div>
          </div>

          <ol className="relative">
            {STEPS.map((step, i) => (
              <TimelineStep
                key={step.n}
                step={step}
                isLast={i === STEPS.length - 1}
              />
            ))}
          </ol>

          {/* End cap */}
          <div className="mt-4 flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-anthracite/60">
            <span className="w-16 h-[1px] bg-gold" />
            Sürecin Sonu · Değer Sürekliliği
          </div>
        </div>
      </section>

      {/* 3. GELİŞTİRDİĞİMİZ ÇÖZÜMLER */}
      <section
        data-testid="yaklasim-cozumler"
        className="bg-anthracite text-white py-24 lg:py-32 relative overflow-hidden"
      >
        <div className="absolute inset-0 blueprint-grid opacity-25 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-[1px] bg-gold" />
                <span className="overline text-gold">
                  Yaklaşım Tipleri
                </span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight leading-tight">
                Geliştirdiğimiz
                <br />
                çözümler.
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-base leading-relaxed text-white/75 font-light max-w-lg">
                Farklı proje tiplerini kendi doğasına özgü metodolojilerle ele
                alırız. Aşağıdaki başlıklar, referans/portföy değil; her tipe
                nasıl yaklaştığımızı özetleyen çerçevelerdir.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[1px] bg-white/10">
            {COZUMLER.map(({ icon: Icon, title, body }, i) => (
              <div
                key={title}
                data-testid={`yaklasim-cozum-${i}`}
                className="group bg-anthracite hover:bg-navy transition-colors p-8 lg:p-12 flex flex-col min-h-[300px]"
              >
                <div className="flex items-start justify-between">
                  <span className="w-12 h-12 border border-gold/50 flex items-center justify-center text-gold shrink-0 group-hover:bg-gold group-hover:text-white transition-colors">
                    <Icon size={20} strokeWidth={1.5} />
                  </span>
                  <span className="w-8 h-[1px] bg-gold/60 mt-6" />
                </div>

                <h3 className="mt-8 font-heading text-xl md:text-2xl font-medium leading-snug">
                  {title}
                </h3>
                <div className="mt-4 w-10 h-[1px] bg-gold/60" />
                <p className="mt-4 text-[14.5px] leading-relaxed text-white/75 font-light">
                  {body}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-white/50">
            <span className="w-8 h-[1px] bg-gold" />
            Referans göstermek yerine yaklaşımımızı paylaşırız.
          </div>
        </div>
      </section>

      {/* 4. ALT CTA */}
      <section
        data-testid="yaklasim-cta"
        className="bg-navy text-white py-24 lg:py-28 relative overflow-hidden"
      >
        <div className="absolute inset-0 blueprint-grid opacity-30 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="overline text-gold mb-5">İletişim</div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight leading-tight">
              Projenizi bu yaklaşımla
              <br />
              nasıl hayata geçirebileceğimizi
              <br />
              <span className="text-gold italic font-extralight">
                konuşalım.
              </span>
            </h2>
            <p className="mt-6 max-w-xl text-white/70 leading-relaxed font-light">
              İhtiyaç analizinden teslim ve değer yönetimine kadar süreci,
              projenizin ölçek ve önceliğine göre birlikte kurgulayabiliriz.
            </p>
          </div>
          <div className="md:col-span-4 md:flex md:justify-end">
            <Link
              to="/iletisim"
              data-testid="yaklasim-cta-iletisim"
              className="group inline-flex items-center gap-3 bg-gold hover:bg-gold-hover text-white px-8 py-4 text-[12.5px] tracking-widest uppercase font-medium transition-colors"
            >
              Bizimle İletişime Geçin
              <ArrowRight
                size={16}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
          </div>
        </div>
      </section>

      {/* Alt küçük geçiş */}
      <section className="bg-white py-10 border-t border-navy/5">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-[12px] tracking-[0.25em] uppercase text-anthracite/60">
            Devamı
          </div>
          <Link
            to="/gayrimenkul-gelistirme"
            data-testid="yaklasim-next-gayrimenkul"
            className="group inline-flex items-center gap-3 text-navy hover:text-gold transition-colors text-[13px] tracking-widest uppercase font-medium"
          >
            Gayrimenkul Geliştirme
            <ArrowUpRight
              size={15}
              className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </Link>
        </div>
      </section>
    </>
  );
}
